import {
  Bike,
  Bus,
  Building2,
  Car,
  Caravan,
  CalendarCheck,
  CalendarDays,
  Check,
  Clock,
  Loader2,
  MapPin,
  Mountain,
  Shuffle,
  SlidersHorizontal,
  Sparkles,
  Train,
  Waves,
  TramFront,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { formatHours } from "../lib/format";
import { LARGE_VEHICLE_METERS } from "../lib/types";
import { MAX_DAYS, floorFor } from "../lib/url";
import type {
  CorridorOption,
  Origin,
  UiLang,
  Pace,
  TravelMode,
  TripForm,
  VehicleKind,
  Vibe,
} from "../lib/types";

interface Props {
  form: TripForm;
  onChange: (patch: Partial<TripForm>) => void;
  onSubmit: () => void;
  loading: boolean;
  modeNotes: Record<TravelMode, string> | null;
  /**
   * Stedene man kan si at man reiser fra.
   *
   * Endepunktene til rutene, som er de stedene folk bor, lander eller kommer
   * fra. Tom liste før korridorene er lastet, og da står feltet ikke i det
   * hele tatt — en velger med ingenting i er verre enn ingen velger.
   */
  origins?: Origin[];
  /**
   * Månedene hvert kjøretøy kan brukes.
   *
   * Meldt fra bruk: «om vinteren er noen kjøretøy, for eksempel motorsykkel,
   * ikke i bruk — fjern eller dem dem ut i vintermånedene». Valget *fjernes*
   * ikke: en motorsykkel i januar er upraktisk, ikke forbudt, og forskjellen
   * er brukerens å ta stilling til. Samme regel som `interestOffSeason` i
   * ruteforslaget. Regelen bor på serveren — se `VEHICLE_SEASON`.
   */
  vehicleSeasons?: Partial<Record<VehicleKind, number[]>>;
  lang: UiLang;
  /** Korridoren som er valgt — avgjør hvilke reisemåter som er mulige. */
  corridor?: CorridorOption;
  /**
   * Fører tilbake til rutevelgeren.
   *
   * Valgfri: skjemaet vises også i tester og sammenhenger uten en velger å
   * gå tilbake til, og en knapp som ikke gjør noe er verre enn ingen knapp.
   */
  onChangeRoute?: () => void;
}

/*
 * Kollektivt får sitt eget ikon, ikke togets.
 *
 * `TramFront` framfor `Train`: valget dekker tog, buss og båt, og å gjenbruke
 * togikonet ville sagt «tog» om det valget som nettopp *ikke* er bare tog —
 * som er hele forskjellen brukeren ba om.
 */
const MODE_ICONS: Record<TravelMode, LucideIcon> = {
  kollektiv: TramFront,
  tog: Train,
  buss: Bus,
  bil: Car,
};
const VIBE_ICONS: Record<Vibe, LucideIcon> = {
  fjell: Mountain,
  fjord: Waves,
  by: Building2,
  miks: Shuffle,
};

const VEHICLE_ICONS: Record<VehicleKind, LucideIcon> = {
  bil: Car,
  bobil: Caravan,
  motorsykkel: Bike,
};

/*
 * Rekkefølgen brukeren møter valgene i, standarden først.
 *
 * Denne lista og hvitelista i lib/url.ts må inneholde det samme. Å legge til
 * her og glemme der er *ikke* en type-feil — en `TravelMode[]` rommer gjerne en
 * delmengde — og symptomet ville vært at brukeren velger kollektivt, lenka
 * skrives som `reise=kollektiv`, hvitelista avviser den, og turen stille endrer
 * seg ved omlasting og ved deling. `url.test.ts` vokter at de er enige.
 */
const MODES: TravelMode[] = ["kollektiv", "tog", "buss", "bil"];
const VEHICLES: VehicleKind[] = ["bil", "bobil", "motorsykkel"];
const VIBES: Vibe[] = ["fjell", "fjord", "by", "miks"];
const PACES: Pace[] = ["avslappet", "aktiv"];

/**
 * Dagvalgene som gir mening for en gitt strekning.
 *
 * Alternativene var fast 2–5, som holdt da den lengste ruta var Bergensbanen
 * på sju timer. Oslo–Tromsø er tretti timer ende til ende: fem dager ville
 * vært ren transport. Anslaget er grovt med vilje — omtrent to timers
 * framdrift per dag levner tid til å faktisk se noe.
 */
export function dayOptions(spanHours: number): number[] {
  if (spanHours >= 24) return [7, 9, 11, 12];
  if (spanHours >= 15) return [5, 6, 8, 10];
  if (spanHours >= 9) return [3, 4, 5, 6];
  return [2, 3, 4, 5];
}

/*
 * Turlengden bor i lib/url.ts, ikke her: en delt lenke og fritekstplanleggeren
 * må svare på det samme spørsmålet uten å gå gjennom dette skjemaet. Se
 * `usableForm`.
 */

/** Dato i dag, som ISO — brukes som nedre grense i datovelgeren. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ControlPanel({
  form,
  onChange,
  onSubmit,
  loading,
  modeNotes,
  lang,
  corridor,
  origins = [],
  vehicleSeasons = {},
  onChangeRoute,
}: Props) {
  const t = dictionaries[lang];
  const note = modeNotes?.[form.mode];
  /*
   * Måneden turen går i, når en dato er satt. Uten dato er ingenting ute av
   * sesong — vi vet ikke når turen skjer, og en advarsel om januar til en som
   * ikke har sagt januar er støy.
   */
  const tripMonth = form.startDate ? Number(form.startDate.slice(5, 7)) : null;
  const offSeason = (vehicle: VehicleKind) =>
    tripMonth != null && (vehicleSeasons[vehicle]?.includes(tripMonth) ?? true) === false;
  const suggested = dayOptions(corridor?.spanHours ?? 7);
  /*
   * Forslagene pluss det som faktisk er valgt.
   *
   * Knappene er tunet etter rutas lengde og er gode svar, men de var også
   * *grensen*: en egendefinert lengde hadde ingen knapp, og raden så ut som
   * om skjemaet hadde glemt hva brukeren trykket. Nå står tallet alltid i
   * raden det ble valgt fra, og stepperen når resten.
   */
  const days = suggested.includes(form.days)
    ? suggested
    : [...suggested, form.days].sort((a, b) => a - b);
  /*
   * Sagt, ikke rettet stille. Før ble en lengde utenfor forslagene snappet til
   * nærmeste knapp, og turen ble en annen enn den brukeren ba om uten at noe
   * sa fra. Nå står valget, og merknaden sier hva det koster.
   */
  const roomy = form.days > suggested[suggested.length - 1];
  const tight = form.days < suggested[0];
  /*
   * Hvilke reisemåter ruta dekker hele veien. Nord for Bodø går det ingen
   * jernbane, så et togvalg dekker bare en del av strekningen der.
   */
  const available = corridor?.modes ?? MODES;
  /*
   * Merknaden når det valgte ikke dekker hele ruta.
   *
   * Vist som *tekst*, ikke som et `title`-attributt på en deaktivert knapp. Den
   * gamle løsningen var usynlig på telefon — der de fleste planlegger — og sa
   * dessuten «dette kan du ikke få», som ikke er sant: serveren utvider et
   * smalt kollektivvalg til kollektivt og legger toget inn der det går.
   */
  const partial =
    /*
     * Bare for de *smale* valgene. Kollektivt kan ikke utvides videre — det er
     * det serveren utvider *til* — så merknaden ville vært meningsløs der. Og
     * bil utvides aldri: et bilvalg faller til bil, og bare fordi det er bil.
     *
     * Sjekken står på valget og ikke bare på `available`, fordi en korridor
     * hentet fra et eldre svar kan mangle «kollektiv» i lista si — og da ville
     * merknaden dukket opp under standardvalget og sagt at ruta blir kollektiv,
     * hvilket den alt er.
     */
    corridor && (form.mode === "tog" || form.mode === "buss") && !available.includes(form.mode)
      ? t.modeWidenedHint(t.travelModeName[form.mode], corridor.name)
      : null;

  return (
    /*
     * `id` er ankeret presentasjonen ruller til når man trykker «planlegg denne
     * ruta»: dialogen lukkes, og siden skal føre blikket ned til valgene man nå
     * skal ta. Uten det ble man stående på toppen og måtte finne skjemaet selv.
     */
    <div className="card no-print" id="plan-form">
      {/* Kortet sto uten overskrift og begynte rett på «Antall dager». */}
      <div className="card-title">
        <SlidersHorizontal size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
        {t.tripLabel}
      </div>
      <p className="card-sub">{t.tripHint}</p>

      {/*
        Hvilken rute valgene gjelder for.

        Meldt fra bruk: man valgte en rute i velgeren over, og skjemaet her nede
        sa ingenting om hvilken. Alle valgene under — hvor mange dager som er
        mulige, hvilke reisemåter som finnes — avhenger av ruta, og et skjema
        som stiller betingede spørsmål uten å si hva betingelsen er, er et
        skjema man ikke kan svare riktig på.

        Knappen fører tilbake opp til velgeren framfor å åpne en ny. Ruta velges
        ett sted, og det stedet er der bildene er.
      */}
      {corridor && (
        <div className="chosen-route">
          {corridor.image && (
            <span className="chosen-route-photo">
              <img src={corridor.image.src} alt="" loading="lazy" aria-hidden="true" />
            </span>
          )}
          <span className="chosen-route-body">
            <span className="chosen-route-label">{t.chosenRoute}</span>
            <strong className="chosen-route-name">
              {corridor.startName}
              <span className="chosen-route-arrow" aria-hidden="true">
                →
              </span>
              {corridor.endName}
            </strong>
            <span className="chosen-route-meta">
              <span>
                <MapPin size={11} /> {t.stopCount(corridor.stopCount)}
              </span>
              <span>
                <Clock size={11} /> {t.endToEnd(formatHours(Math.round(corridor.spanHours), lang))}
              </span>
            </span>
          </span>
          {onChangeRoute && (
            <button type="button" className="chosen-route-change" onClick={onChangeRoute}>
              {t.changeRoute}
            </button>
          )}
        </div>
      )}

      {/*
        «Hvor reiser du fra?»

        Meldt fra bruk: «hva om jeg har lyst til å starte rute fra Stavanger, men
        jeg bor i Oslo». Alle rutene begynner et sted i Norge, og appen antok at
        den reisende alt sto der — etappen fram dit fantes ikke i planen.

        Før dagvalget, fordi det er spørsmålet som kommer først når man
        planlegger: hvor starter jeg, så hvor lenge. Feltet står ikke i det hele
        tatt før listen er lastet; en velger med ingenting i er verre enn ingen.
      */}
      {origins.length > 0 && (
        <div className="field">
          <label className="field-label" htmlFor="from-stop">
            {t.fromLabel}
          </label>
          <select
            id="from-stop"
            className="date-input"
            value={form.fromStopId}
            onChange={(event) => onChange({ fromStopId: event.target.value })}
          >
            {/* Tomt valg først: de fleste starter der ruta starter. */}
            <option value="">{t.fromNone}</option>
            {origins.map((origin) => (
              <option key={origin.id} value={origin.id}>
                {origin.name}
              </option>
            ))}
          </select>
          <span className="field-hint">{t.fromHint}</span>
        </div>
      )}

      {/*
        «Og hjem igjen?»

        Meldt fra bruk: «hva med å komme tilbake? Om jeg må tilbake til Oslo og
        vil ha en tur på fem dager, hvordan påvirker det planleggeren?»

        Rett under «hvor reiser du fra?», fordi det er det samme spørsmålet sett
        fra den andre enden — og fordi de fleste svarer det samme på begge. Tomt
        valg først: den som ikke har tenkt på hjemreisen skal ikke tvinges til å
        ta stilling til den for å få en plan.

        Hva det koster, står i planen og ikke her: timetallet avhenger av hvilket
        stopp turen faktisk ender på, og det vet vi først når planen finnes.
      */}
      {origins.length > 0 && (
        <div className="field">
          <label className="field-label" htmlFor="return-stop">
            {t.returnLabel}
          </label>
          <select
            id="return-stop"
            className="date-input"
            value={form.returnToStopId}
            onChange={(event) => onChange({ returnToStopId: event.target.value })}
          >
            <option value="">{t.returnNone}</option>
            {origins.map((origin) => (
              <option key={origin.id} value={origin.id}>
                {origin.name}
              </option>
            ))}
          </select>
          <span className="field-hint">{t.returnHint}</span>
        </div>
      )}

      <div className="field">
        <label className="field-label" htmlFor="days-group">
          {t.daysLabel}
        </label>
        <div className="segmented" id="days-group" role="group" aria-label={t.daysLabel}>
          <button
            type="button"
            className="seg seg--step"
            aria-label={t.daysFewer}
            /*
             * Gulvet avhenger av hva slags plan det er, ikke av et fast tall.
             * Sto det `MIN_DAYS` her, kunne man ikke trekke en Trolltunga-plan
             * ned til den dagsturen folk faktisk tar.
             */
            disabled={form.days <= floorFor(form)}
            onClick={() => onChange({ days: Math.max(floorFor(form), form.days - 1) })}
          >
            &minus;
          </button>
          {days.map((day) => (
            <button
              key={day}
              type="button"
              className={`seg ${form.days === day ? "seg--on" : ""}`}
              aria-pressed={form.days === day}
              onClick={() => onChange({ days: day })}
            >
              {t.daysOption(day)}
            </button>
          ))}
          <button
            type="button"
            className="seg seg--step"
            aria-label={t.daysMore}
            disabled={form.days >= MAX_DAYS}
            onClick={() => onChange({ days: Math.min(MAX_DAYS, form.days + 1) })}
          >
            +
          </button>
        </div>
        {roomy && <p className="note">{t.daysRoomy(form.days)}</p>}
        {tight && <p className="note">{t.daysTight(form.days)}</p>}
      </div>

      <div className="field">
        <span className="field-label">{t.modeLabel}</span>
        <div className="segmented" role="group" aria-label={t.modeLabel}>
          {MODES.map((mode) => {
            const Icon = MODE_ICONS[mode];
            const active = form.mode === mode;
            const usable = available.includes(mode);
            return (
              <button
                key={mode}
                type="button"
                /*
                 * Ikke `disabled` lenger, og ikke en `title`.
                 *
                 * Flisa var deaktivert med forklaringen i et `title`-attributt,
                 * og en `title` på en `disabled`-knapp vises *aldri* på en
                 * telefon — som er der de fleste planlegger. Forklaringen var
                 * altså usynlig nettopp der den trengtes.
                 *
                 * Verre: å deaktivere den sier «dette kan du ikke få», og det
                 * er ikke sant lenger. Serveren utvider et smalt kollektivvalg
                 * til kollektivt og legger toget inn på den delen som har
                 * jernbane — så svaret er «du kan spørre, og du får toget der
                 * det går». Merket under sier det, som synlig tekst.
                 */
                className={`seg-tile ${active ? "seg-tile--on" : ""} ${
                  usable ? "" : "seg-tile--partial"
                }`}
                aria-pressed={active}
                onClick={() => onChange({ mode })}
              >
                {active && (
                  <span className="seg-tile-check" aria-hidden="true">
                    <Check size={11} strokeWidth={3.5} />
                  </span>
                )}
                <Icon size={20} />
                <span>{t.travelModeName[mode]}</span>
              </button>
            );
          })}
        </div>
        {note && <p className="note">{note}</p>}
        {partial && <p className="note note--partial">{partial}</p>}
      </div>

      {/*
        Kjøretøyvelgeren, bare for den som kjører selv.

        Den står under reisemåten og forsvinner når man har valgt tog eller
        buss: hvor lang bobilen er, er et ikke-spørsmål for en togreisende, og
        et valg man ikke kan handle på er verre enn ingen valg.

        Den endrer ingen stopp. Det står i hintet med vilje — en velger som ser
        ut som den kan gi en annen rute, og ikke gjør det, er et løfte som
        brytes.
      */}
      {form.mode === "bil" && (
        <div className="field">
          <span className="field-label">{t.vehicleLabel}</span>
          <div className="segmented" role="group" aria-label={t.vehicleLabel}>
            {VEHICLES.map((vehicle) => {
              const Icon = VEHICLE_ICONS[vehicle];
              const active = form.vehicle === vehicle;
              const off = offSeason(vehicle);
              return (
                <button
                  key={vehicle}
                  type="button"
                  className={`seg-tile ${active ? "seg-tile--on" : ""} ${off ? "seg-tile--off" : ""}`}
                  aria-pressed={active}
                  /*
                   * `aria-disabled` og ikke `disabled`: valget står. En
                   * motorsykkel i januar er upraktisk, ikke forbudt, og en
                   * knapp som ikke lar seg trykke kan heller ikke forklare seg
                   * på berøring — samme avveining som interessene.
                   */
                  aria-disabled={off || undefined}
                  onClick={() => onChange({ vehicle })}
                >
                  {active && (
                    <span className="seg-tile-check" aria-hidden="true">
                      <Check size={11} strokeWidth={3.5} />
                    </span>
                  )}
                  <Icon size={20} />
                  <span>
                    {t.vehicleName[vehicle]}
                    {/* Grensen er det skiltene går på, så den står i klartekst. */}
                    {vehicle === "bobil" && ` (>${LARGE_VEHICLE_METERS} m)`}
                  </span>
                  {/* Grunnen står i knappen, ikke i et `title` — et `title` er
                      usynlig på berøring, og det er der det trengs. */}
                  {off && tripMonth != null && (
                    <small className="seg-tile-off">
                      {t.vehicleOffSeason(t.monthName[tripMonth - 1])}
                    </small>
                  )}
                </button>
              );
            })}
          </div>
          {/*
            Hvorfor, i klartekst, når det gjelder det valgte kjøretøyet.
            «Sjelden i januar» sier hva; dette sier hvorfor og hva planen
            likevel regner med.
          */}
          {offSeason(form.vehicle) && tripMonth != null && (
            <p className="note note--warn">
              {t.vehicleOffSeasonWhy(t.vehicleName[form.vehicle], t.monthName[tripMonth - 1])}
            </p>
          )}
          <p className="note">{t.vehicleHint}</p>
        </div>
      )}

      <div className="field">
        <span className="field-label">{t.vibeLabel}</span>
        <div className="chips" role="group" aria-label={t.vibeLabel}>
          {VIBES.map((vibe) => {
            const Icon = VIBE_ICONS[vibe];
            const active = form.vibe === vibe;
            return (
              <button
                key={vibe}
                type="button"
                className={`chip ${active ? "chip--on" : ""}`}
                aria-pressed={active}
                onClick={() => onChange({ vibe })}
              >
                <Icon size={15} />
                {t.vibeName[vibe]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="field">
        <span className="field-label">{t.paceLabel}</span>
        <div className="segmented" role="group" aria-label={t.paceLabel}>
          {PACES.map((pace) => {
            const active = form.pace === pace;
            return (
              <button
                key={pace}
                type="button"
                className={`seg seg--stacked ${active ? "seg--on" : ""}`}
                aria-pressed={active}
                onClick={() => onChange({ pace })}
              >
                <strong>{t.paceName[pace]}</strong>
                <small>{t.paceDetail[pace]}</small>
              </button>
            );
          })}
        </div>
      </div>

      {/*
        Datoen er det enkeltvalget som låser opp mest — vær, dagslys, nordlys
        og faktiske avganger — men sto som et lite grått felt blant de andre.
        Egen ramme, så det leses som en invitasjon framfor en detalj.
      */}
      <div className="field field--date">
        <label className="field-label" htmlFor="start-date">
          <CalendarDays size={13} />
          {t.dateLabel}
          <span className="field-hint">{t.dateHint}</span>
        </label>
        <div className="date-row">
          <input
            id="start-date"
            type="date"
            className="date-input"
            value={form.startDate}
            min={today()}
            onChange={(event) => {
              const startDate = event.target.value;
              // Vær- og dagvalg gir ingen mening uten dato.
              onChange({
                startDate,
                optimizeForWeather: startDate ? form.optimizeForWeather : false,
                suggestBestDay: startDate ? form.suggestBestDay : false,
              });
            }}
          />
          {form.startDate && (
            <button
              type="button"
              className="link-button"
              onClick={() =>
                onChange({ startDate: "", optimizeForWeather: false, suggestBestDay: false })
              }
            >
              {t.clearDate}
            </button>
          )}
        </div>
      </div>

      <div className="toggle-stack">
        <button
          type="button"
          className={`toggle ${form.optimizeForWeather ? "toggle--on" : ""}`}
          aria-pressed={form.optimizeForWeather}
          disabled={!form.startDate}
          onClick={() => onChange({ optimizeForWeather: !form.optimizeForWeather })}
        >
          <span className="toggle-switch" aria-hidden="true" />
          <span className="toggle-body">
            <strong>
              <Sparkles size={13} />
              {t.weatherToggleTitle}
            </strong>
            <span>{form.startDate ? t.weatherToggleOn : t.weatherToggleOff}</span>
          </span>
        </button>

        <button
          type="button"
          className={`toggle ${form.suggestBestDay ? "toggle--on" : ""}`}
          aria-pressed={form.suggestBestDay}
          disabled={!form.startDate}
          onClick={() => onChange({ suggestBestDay: !form.suggestBestDay })}
        >
          <span className="toggle-switch" aria-hidden="true" />
          <span className="toggle-body">
            <strong>
              <CalendarCheck size={13} />
              {t.bestDayToggleTitle}
            </strong>
            <span>{form.startDate ? t.bestDayToggleOn : t.bestDayToggleOff}</span>
          </span>
        </button>
      </div>

      <button type="button" className="generate" onClick={onSubmit} disabled={loading}>
        {loading ? (
          <>
            <Loader2 size={17} className="spin" /> {t.generating}
          </>
        ) : (
          t.generate
        )}
      </button>
    </div>
  );
}
